import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft } from "lucide-react";
import Logo from "@/components/Logo";
import { api, LOGO_URL } from "@/lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
      toast.success("If that email exists, a reset link has been sent.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen grid-cols-1 md:grid-cols-2">
      <div className="relative hidden flex-col justify-between bg-primary p-12 md:flex">
        <Link to="/"><Logo /></Link>
        <div>
          <img src={LOGO_URL} alt="" className="h-40 w-40 rounded-[22%] shadow-2xl" />
          <h2 className="mt-10 max-w-sm font-display text-4xl leading-tight tracking-tight">
            Forgot
            <br />
            <span className="italic">your</span> way back?
          </h2>
          <p className="mt-4 max-w-sm text-sm text-foreground/70">
            No stress. We&rsquo;ll send a link to set a new password.
          </p>
        </div>
        <div className="text-xs uppercase tracking-[0.22em] text-foreground/60">60‑minute link · one‑time use</div>
      </div>

      <div className="flex flex-col justify-center bg-white px-6 py-12 md:px-16">
        <div className="mx-auto w-full max-w-sm">
          <div className="mb-10 md:hidden"><Logo /></div>

          {sent ? (
            <div data-testid="forgot-sent">
              <p className="text-xs font-medium uppercase tracking-[0.22em] text-muted-foreground">Check your inbox</p>
              <h1 className="mt-3 font-display text-4xl tracking-tight md:text-5xl">Link sent.</h1>
              <p className="mt-5 max-w-md text-sm leading-relaxed text-muted-foreground">
                If an account exists for <span className="font-medium text-foreground">{email}</span>, you&rsquo;ll get an email with a reset link in a minute or two. The link expires in 60 minutes.
              </p>
              <Link
                to="/login"
                data-testid="forgot-back-to-login"
                className="mt-8 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
              >
                <ArrowLeft className="h-4 w-4" /> Back to sign in
              </Link>
            </div>
          ) : (
            <>
              <p className="text-xs font-medium uppercase tracking-[0.22em] text-muted-foreground">Reset password</p>
              <h1 className="mt-3 font-display text-4xl tracking-tight md:text-5xl">Forgot it?</h1>
              <p className="mt-4 max-w-md text-sm text-muted-foreground">
                Enter your email and we&rsquo;ll send a link to set a new password.
              </p>

              <form onSubmit={submit} className="mt-10 space-y-6" data-testid="forgot-form">
                <label className="block">
                  <div className="mb-2 text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">Email</div>
                  <input
                    type="email"
                    required
                    data-testid="forgot-email-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="w-full border-b-2 border-border bg-transparent py-3 text-lg outline-none focus:border-primary"
                  />
                </label>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="forgot-submit-button"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-foreground px-6 py-4 text-sm font-medium text-background transition-transform hover:bg-foreground/90 active:scale-95 disabled:opacity-60"
                >
                  {loading ? "Sending..." : "Send reset link"} <ArrowRight className="h-4 w-4" />
                </button>
              </form>

              <Link
                to="/login"
                data-testid="forgot-to-login"
                className="mt-8 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
              >
                <ArrowLeft className="h-4 w-4" /> Back to sign in
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
